/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 * Includes scroll position restoration when navigating back from product pages.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();

      // Storage key based on current collection URL (without page param)
      this.storageKey = 'pagination:' + window.location.pathname + window.location.search.replace(/[?&]page=\d+/, '');

      this.init();
      this.bfcacheHandler = this.handleBfcache.bind(this);
      document.addEventListener('on:bfcache:load-restore', this.bfcacheHandler);

      // Save state before page is cached/unloaded (catches swipe-back, tab close, etc.)
      this.pagehideHandler = () => { this.saveState(); };
      window.addEventListener('pagehide', this.pagehideHandler);

      // Restore scroll position on initial load (non-bfcache)
      this.restoreState();
    }

    disconnectedCallback() {
      this.destroy();
      document.removeEventListener('on:bfcache:load-restore', this.bfcacheHandler);
      window.removeEventListener('pagehide', this.pagehideHandler);
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;
      this.currentPage = 1;

      // Bind click handlers to pagination results (product link clicks)
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();

                  // Continue to load results as long as the 'load more' button is visible
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Handle bfcache restore — browser kept DOM intact, just re-init observers.
     * Also restore scroll position if browser reset it (Safari swipe-back).
     */
    handleBfcache() {
      this.destroy();
      this.init();

      // Restore scroll position if browser didn't preserve it
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (raw) {
          const saved = JSON.parse(raw);
          // Update currentPage so future saveState() has the right value
          if (saved.page) this.currentPage = saved.page;
          // If scroll is near top but we had a saved position, restore it
          if (saved.scrollY && window.scrollY < 100) {
            requestAnimationFrame(() => {
              window.scrollTo(0, saved.scrollY);
            });
          }
        }
      } catch (e) {
        // Ignore
      }
    }

    /**
     * Save pagination state (current page + scroll position) to sessionStorage
     */
    saveState() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          page: this.currentPage,
          scrollY: window.scrollY
        }));
      } catch (e) {
        // sessionStorage unavailable or full
      }
    }

    /**
     * Restore pagination state: load all previously loaded pages and scroll to saved position
     */
    async restoreState() {
      let saved;
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return;
        saved = JSON.parse(raw);
        sessionStorage.removeItem(this.storageKey);
      } catch (e) {
        return;
      }

      if (!saved || !saved.scrollY) return;

      // Load additional pages if user had clicked "load more"
      if (saved.page && saved.page > 1) {
        this.isRestoring = true;
        for (let p = 2; p <= saved.page; p++) {
          if (!this.loadMoreButton || !this.loadMoreButton.href) break;
          await this.loadMore(true);
        }
        this.isRestoring = false;
      }

      // Scroll to saved position — double rAF ensures layout is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, saved.scrollY);
        });
      });
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on an item in the pagination results.
     * Saves scroll position before navigating to product page.
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      const link = evt.target.closest('a');
      const result = evt.target.closest('.js-pagination-result');

      if (link && result) {
        // Save state before navigating away
        this.saveState();

        if (this.controller) {
          this.controller.abort();
        }
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     * @param {boolean} silent - If true, skip loading UI (used during restore)
     */
    async loadMore(silent = false) {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          if (!silent) {
            this.loadMoreButton.classList.add('is-loading');
            this.loadMoreButton.setAttribute('aria-disabled', 'true');

            if (this.pagination.dataset.paginationStyle === 'traditional') {
              this.pagination.classList.add('is-loading');
            }
          }

          // Retrieve and parse the next page
          const response = await fetch(this.loadMoreButton.href, {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Append the additional results
          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          this.results = this.querySelectorAll('.js-pagination-result');

          // Track current page number
          this.currentPage++;

          // Update the pagination content
          const newPagination = tmpl.content.querySelector('.js-pagination');
          if (newPagination.dataset.isMoreResults) {
            this.pagination.innerHTML = newPagination.innerHTML;

            // Rebind to the new load more button
            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
            }
          } else {
            this.pagination.remove();
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (!silent && this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (!silent && this.pagination && this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
